import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import Session from "users/__session/session";
import { AccountType } from "users/account-types/account-type";
import { useGetUserInformationQuery } from "users/information/_api/user-information-api";
import UserInformation from "users/information/user-information";

export const SessionContext = createContext<Session>(undefined!);

export function SessionProvider({ children }: { readonly children: React.ReactNode }) {
  const [session, setSession] = useState<Session>();

  const userInformationQuery = useGetUserInformationQuery({ session: session! }, { skip: !session?.isLoggedIn });

  useEffect(() => {
    const initializeSession = async () => {
      const initialSession = Session.loadFromStorage(setSession);

      let accountType: AccountType;
      if (initialSession.context?.viewingAsVendor === undefined) {
        accountType = initialSession.context?.isVendor ? AccountType.Vendor : AccountType.Client;
      } else {
        accountType = initialSession.context.viewingAsVendor ? AccountType.Vendor : AccountType.Client;
      }
      await initialSession.setAccountViewType(accountType);
      setSession(initialSession);
    };
    void initializeSession();
  }, []);

  useEffect(() => {
    if (session?.isLoggedIn && userInformationQuery.data) {
      const userInfo = UserInformation.fromJSON(userInformationQuery.data);
      if(!userInfo) {
        throw new Error("Failed to parse user information from API");
      }
      session.refreshFromUserInfo(userInfo);
    }
  }, [userInformationQuery.data, session?.isLoggedIn]);

  return (
    <SessionContext.Provider value={session!}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): Session {
  let context = useContext?.(SessionContext);

  //#2188 bug fix for useSession being called before the context is initialized in the SessionProvider 
  //removing the session instantiation will result in a provider error.
  if (!context) {
    context = Session.loadFromStorage(() => { console.warn("Session not initialized yet") });
  }

  return context;
}

export function RequireSession({ children }: { readonly children: React.JSX.Element }) {
  const session = useSession();
  const location = useLocation();

  if (!session.isLoggedIn) {
    // Redirect user to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to forward them
    // along to that page after they log in.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}